import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileError } from 'react-dropzone';
import { AppButton, Box, FormProvider, Preloader, ShimmerBox, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { BackButton, Modal } from '@/components';
import ScanPreviewContainer from '@/components/elements/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/elements/DropScan/useFilesInDocument.store';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import theme from '@/styles/theme';
import { ICreateDocumentDtoDocumentTypeEnum } from '@/types';

import { useClaimsSearchParticipantQuery } from '../../../../queries';
import ChangeFile from '../../../ChangeFile';
import {
  useLettersOfMedicalNecessityForm,
  useSubmitLettersOfMedicalNecessityPopup,
  useViewLmnItem,
} from '../../hooks';
import {
  LettersOfMedicalNecessityForm,
  LettersOfMedicalNecessityFormField,
} from '../../hooks/useLettersOfMedicalNecessityForm';
import {
  LettersOfMedicalNecessity,
  LMN_UPLOAD_STORE_KEY,
  LMNPopupMode,
  LMNPopupStep,
} from '../../LettersOfMedicalNecessity.types';
import { useUpdateLmnItemQuery } from '../../queries';
import { useLettersOfMedicalNecessityPopupStore } from '../../stores';
import { LettersOfMedicalNecessityPopupState } from '../../stores/useLettersOfMedicalNecessityPopup.store';

import DocumentPreviewPopupForm from './DocumentPreviewPopupForm';
import { useDocumentPreviewNodes } from './hooks';

interface Props {
  mode: LMNPopupMode;
  onSetVisible: (value: boolean) => void;
  setStep?: (step: LMNPopupStep) => void;
  onSuccess?: (lmnID: string) => void;
  itemId?: string;
  onEdit?: () => void;
  onSuccessEdit?: (value?: LettersOfMedicalNecessity) => void;
  setIsError?: (value: boolean) => void;
  onFileUploadError?: (error?: FileError) => void;
  onDelete?: (lmnId: string) => void;
  isMobile?: boolean;
  createForClaimId?: string;
  employeeId?: string;
  disabledClaimSelect?: boolean;
}

const DocumentPreviewPopup: React.FC<Props> = ({
  mode,
  onSetVisible,
  onSuccess,
  setStep,
  itemId,
  onEdit,
  onSuccessEdit,
  setIsError,
  onFileUploadError,
  onDelete,
  isMobile,
  createForClaimId,
  employeeId,
  disabledClaimSelect,
}) => {
  const { methods } = useLettersOfMedicalNecessityForm({ isViewMode: mode === LMNPopupMode.VIEW });
  const { updateLMN } = useUpdateLmnItemQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { values, handleReset } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);

  const { sourceState, setSourceState } = useLettersOfMedicalNecessityPopupStore();
  const [isLoading, setIsLoading] = useState(false);
  const { claims } = useClaimsSearchParticipantQuery({
    page: 1,
    perPage: 1,
    disabled: mode === LMNPopupMode.VIEW || !!createForClaimId,
  });
  const { handleCreateLMN, handleSuccessfullyCreated } = useSubmitLettersOfMedicalNecessityPopup({
    onFileUploadError,
    mode,
  });
  const { lmnItem, isLoading: isViewLoading } = useViewLmnItem({ id: itemId });
  const nextRef = useRef<() => Promise<void>>(undefined);

  const hasAvailableClaims = useMemo(() => !!claims?.length, [claims?.length]);

  const allFiles = useMemo(() => (values?.files?.length ? values?.files : []), [values?.files]);
  const file = useMemo(() => allFiles?.[0], [allFiles]);

  const handleSubmitEdit = useCallback(
    async (formValues: LettersOfMedicalNecessityForm) => {
      if (!itemId) return;
      try {
        setIsLoading(true);
        const validDates = formValues[LettersOfMedicalNecessityFormField.validDates];
        const validFromRaw = validDates?.[0];
        const validToRaw = validDates?.[1];
        const hasValidDateRange = validFromRaw && validToRaw;
        const updatedLMN = await updateLMN({
          id: itemId,
          description: formValues[LettersOfMedicalNecessityFormField.description],
          name: formValues[LettersOfMedicalNecessityFormField.name],
          validFrom: hasValidDateRange ? dayjs(validFromRaw).toISOString() : null,
          validTo: hasValidDateRange ? dayjs(validToRaw).toISOString() : null,
        });
        onSuccessEdit?.(updatedLMN);
        handleAddPermanentSnackbar({
          text: `${formValues[LettersOfMedicalNecessityFormField.name]} was edited.`,
          state: SnackbarState.positive,
          closeIcon: true,
          autoRemove: true,
        });
        onSetVisible(false);
      } finally {
        setIsLoading(false);
      }
    },
    [itemId, updateLMN, handleAddPermanentSnackbar, onSetVisible, onSuccessEdit]
  );

  const handleNext = useCallback(
    async (formValues: LettersOfMedicalNecessityForm) => {
      setIsLoading(true);

      const formData: LettersOfMedicalNecessityPopupState = {
        name: formValues[LettersOfMedicalNecessityFormField.name] || '',
        description: formValues[LettersOfMedicalNecessityFormField.description],
        documentType: formValues[
          LettersOfMedicalNecessityFormField.type
        ] as ICreateDocumentDtoDocumentTypeEnum,
        employeeId: employeeId,
        validDates: formValues[LettersOfMedicalNecessityFormField.validDates],
        claimIds: createForClaimId ? [createForClaimId] : ([] as string[]),
      };

      if (hasAvailableClaims && !createForClaimId && !disabledClaimSelect) {
        setSourceState(formData);
        setStep?.(LMNPopupStep.ASSOCIATE_WITH_CLAIMS);
        setIsLoading(false);
        return;
      }

      try {
        const createdLMN = await handleCreateLMN(formData);
        if (!createdLMN?.id) {
          return;
        }
        onSuccess?.(createdLMN?.id || '');
        handleSuccessfullyCreated(createForClaimId);
        onSetVisible(false);
      } catch {
        nextRef.current = async () => {
          await handleNext(formValues);
        };
        setSourceState({
          ...sourceState,
          onError: async () => {
            await nextRef.current?.();
          },
        });
        setIsError?.(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      employeeId,
      setSourceState,
      setStep,
      handleCreateLMN,
      onSetVisible,
      setIsError,
      handleSuccessfullyCreated,
      onSuccess,
      hasAvailableClaims,
      sourceState,
      createForClaimId,
      disabledClaimSelect,
    ]
  );

  const handleClose = useCallback(() => {
    handleReset();
    onSetVisible(false);
  }, [onSetVisible, handleReset]);

  const handleDelete = useCallback(() => {
    if (itemId) {
      onSetVisible(false);
      onDelete?.(itemId);
    }
  }, [onSetVisible, onDelete, itemId]);
  const { title, nextButtonTitle } = useDocumentPreviewNodes({
    mode,
    isLoading,
    fileName: file?.name,
    createForClaimId,
  });

  const validDates = useMemo(() => {
    if (!lmnItem?.validFrom || !lmnItem?.validTo) return [];
    return [];
  }, [lmnItem?.validFrom, lmnItem?.validTo]);

  useEffect(() => {
    if (
      (mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT || mode === LMNPopupMode.PARTIAL_EDIT) &&
      lmnItem
    ) {
      methods.setValue(
        LettersOfMedicalNecessityFormField.type,
        ICreateDocumentDtoDocumentTypeEnum.LETTER_OF_MEDICAL_NECESSITY
      );

      methods.setValue(LettersOfMedicalNecessityFormField.name, lmnItem?.name || file?.name || '');
      methods.setValue(LettersOfMedicalNecessityFormField.description, lmnItem?.description || '');
      const fromDate = lmnItem?.validFrom ? dayjs(lmnItem?.validFrom).toDate() : undefined;
      const toDate = lmnItem?.validTo ? dayjs(lmnItem?.validTo).toDate() : undefined;
      methods.setValue(
        LettersOfMedicalNecessityFormField.validDates,
        fromDate && toDate ? [fromDate, toDate] : []
      );
    }
  }, [mode, file, methods, lmnItem, validDates]);

  useEffect(() => {
    if ((mode === LMNPopupMode.UPLOAD || mode === LMNPopupMode.CONVERT) && file?.name) {
      methods.setValue(LettersOfMedicalNecessityFormField.name, file?.name || '');
    }
  }, [mode, file?.name, methods]);
  return (
    <Modal
      isCloseButtonHidden={isMobile}
      testId={`lmn-document-preview_${mode.toLocaleLowerCase()}`}
      visible
      onSetVisible={handleClose}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box margin={{ bottom: 'spacing16' }}>
          <BackButton testId="lmn-popup" onClick={handleClose} title="Letters of Medical Necessity" />
        </Box>
      ) : null}

      <Text
        size="2xl"
        textAlign={isMobile ? 'start' : 'center'}
        weight={700}
        wordBreak="break-word"
        data-testId="title_modal"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          paddingRight: isMobile ? undefined : theme.spacings.spacing12,
        }}
      >
        {title}
      </Text>

      <Box width={{ max: 'none' }} margin={{ horizontal: isMobile ? '-14px' : undefined }}>
        {(mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT || mode === LMNPopupMode.PARTIAL_EDIT) &&
        isViewLoading ? (
          <Box margin={{ top: isMobile ? 'spacing16' : 'spacing24' }} gap="spacing24">
            {mode === LMNPopupMode.PARTIAL_EDIT ? null : <ShimmerBox height="591px" width="100%" />}
            <ShimmerBox
              height={mode === LMNPopupMode.PARTIAL_EDIT || mode === LMNPopupMode.EDIT ? '294px' : '248px'}
              width="100%"
            />
          </Box>
        ) : (
          <>
            {mode === LMNPopupMode.PARTIAL_EDIT ? null : (
              <Box
                margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
                pad={isMobile ? 'spacing16' : 'spacing24'}
                background="module"
                round="moduleRound"
              >
                <ChangeFile
                  isViewMode={
                    mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT || mode === LMNPopupMode.CONVERT
                  }
                  onFileUploadError={onFileUploadError}
                  storeKey={LMN_UPLOAD_STORE_KEY}
                />
                <Box margin={{ top: 'spacing24' }}>
                  <ScanPreviewContainer
                    defaultFiles={allFiles}
                    activeFile={0}
                    isActiveFileShown
                    disableUpload
                    isRemovingDisabled
                    isShowTooltip={false}
                    showPagesInPreview
                    storeKey={LMN_UPLOAD_STORE_KEY}
                    isFixedDocumentSize
                  />
                </Box>
              </Box>
            )}

            <Box
              data-testId="form-wrapper_modal"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
              pad={isMobile ? 'spacing16' : 'spacing24'}
              background="module"
              round="moduleRound"
            >
              <FormProvider {...methods}>
                <DocumentPreviewPopupForm mode={mode} createForClaimId={createForClaimId} />
              </FormProvider>
            </Box>
          </>
        )}
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        gap="spacing12"
        direction="row"
        align="center"
      >
        {onDelete && mode === LMNPopupMode.VIEW ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_delete"
              width={isMobile ? '100%' : '160px'}
              buttonType="secondary"
              color="red"
              onClick={handleDelete}
            >
              Delete
            </AppButton>
          </Box>
        ) : null}

        <Box flex={isMobile} margin={{ left: 'auto' }}>
          <AppButton
            testId="lmn-document-preview_close"
            width={isMobile ? '100%' : '160px'}
            buttonType="secondary"
            onClick={handleClose}
          >
            Close
          </AppButton>
        </Box>

        {mode === LMNPopupMode.VIEW && onEdit ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_edit"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={onEdit}
            >
              Edit
            </AppButton>
          </Box>
        ) : null}

        {mode === LMNPopupMode.UPLOAD || mode === LMNPopupMode.CONVERT ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_next"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={methods.handleSubmit(handleNext)}
              disabled={isLoading}
            >
              {nextButtonTitle}
            </AppButton>
          </Box>
        ) : null}

        {mode === LMNPopupMode.EDIT || mode === LMNPopupMode.PARTIAL_EDIT ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_save"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={methods.handleSubmit(handleSubmitEdit)}
              disabled={isLoading}
            >
              {isLoading ? <Preloader color="white" /> : 'Save'}
            </AppButton>
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
};

export default DocumentPreviewPopup;
